import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { depoisDoClique, meuUsuarioId } from '../triagem/acaoTriagem';

// IDs fixos dos status de destino
const STATUS_ERRO_SOLAR = '7813a8c2-080e-4ec9-bb83-5347af1af677';
const STATUS_VALIDACAO_PENDENTE = 'cf7ee612-8a50-429e-9e57-fba09ae2c9d9';
const STATUS_TT_RECURSOS_INVALIDOS = '76c803dd-a1d0-4564-95f0-4f8020ecde2d';
const STATUS_CREDITO_REPROVADO = 'e2e50ff1-43df-4ead-b0c7-678e0874690e';

// Nova interface focada em ITEM individual
export interface ItemTriagem {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  numero_cotacao_operadora: string;
  produto: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
  entrada_coluna_em: string;
  coluna_atual: string;
  quadro_atual: string;
  status_id: string;
  data_agendamento: string | null;
  is_first_of_cnpj: boolean;
  /** 69a — o grupo visual é o PEDIDO: itens seguintes ficam indentados. */
  is_first_of_pedido: boolean;
  item_index: number;
}

/* O PEDIDO ANDA INTEIRO NA TRIAGEM (Douglas 24/10/2026, maquete aprovada): a
   linha da triagem é o pedido, com a cotação, os produtos, a quantidade e o
   valor somados. Antes a lista era por linha e os botões andavam pela cotação;
   pedido sem cotação ainda movia só a linha clicada (o 2610210070, de 12
   linhas, pediu 14 justificativas). Agora todo botão leva as linhas do pedido
   que estão nesta coluna, num UPDATE só: anda tudo ou nada. */
export interface PedidoTriagemSolar {
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  tipo_pessoa: string | null;
  documento: string;
  razao_social: string;
  coluna_atual: string;
  entrada_coluna_em: string;
  data_agendamento: string | null;
  cotacoes: string[];
  quantidade: number;
  valor: number;
  itens: ItemTriagem[];
}

export function agruparPorPedido(itens: ItemTriagem[]): PedidoTriagemSolar[] {
  const grupos = new Map<string, PedidoTriagemSolar>();
  for (const i of itens) {
    const chave = `${i.pedido_id}|${i.coluna_atual}`;
    let g = grupos.get(chave);
    if (!g) {
      g = {
        pedido_id: i.pedido_id, numero_pedido: i.numero_pedido, cnpj: i.cnpj, tipo_pessoa: i.tipo_pessoa,
        documento: i.documento, razao_social: i.razao_social, coluna_atual: i.coluna_atual,
        entrada_coluna_em: i.entrada_coluna_em, data_agendamento: i.data_agendamento,
        cotacoes: [], quantidade: 0, valor: 0, itens: [],
      };
      grupos.set(chave, g);
    }
    g.itens.push(i);
    g.quantidade += i.quantidade || 0;
    g.valor += i.subtotal || 0;
    if (i.entrada_coluna_em && (!g.entrada_coluna_em || i.entrada_coluna_em < g.entrada_coluna_em)) g.entrada_coluna_em = i.entrada_coluna_em;
    if (!g.data_agendamento && i.data_agendamento) g.data_agendamento = i.data_agendamento;
    const cot = i.numero_cotacao_operadora?.trim();
    if (cot && !g.cotacoes.includes(cot)) g.cotacoes.push(cot);
  }
  return Array.from(grupos.values());
}

const COLUNAS_INPUT = ['INPUT REALIZADO MOVEL', 'INPUT REALIZADO FIXA'];
const rotuloPedido = (n: number, numero: string) => (n > 1 ? `Pedido ${numero} (${n} linhas)` : `Pedido ${numero}`);

export function useTriagemSolar(familia?: 'MOVEL' | 'FIXA') {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  // Determinar a coluna baseado na familia
  const colunaFiltro = familia === 'MOVEL' ? 'INPUT REALIZADO MOVEL' 
    : familia === 'FIXA' ? 'INPUT REALIZADO FIXA' 
    : null;

  // Buscar ITENS na coluna INPUT REALIZADO (filtrado por familia quando especificado)
  const { data: itens = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-solar', familia || 'todas'],
    queryFn: async () => {
      let query = supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, entrada_coluna_em, produto, manter_em, numero_cotacao_operadora, quantidade, plano_valor, subtotal, data_agendamento, coluna_atual, quadro_atual, status_id, tipo_pessoa, documento')
        .eq('quadro_atual', 'TRAMITACAO_VIVO');

      if (colunaFiltro) {
        query = query.eq('coluna_atual', colunaFiltro);
      } else {
        query = query.in('coluna_atual', ['INPUT REALIZADO MOVEL', 'INPUT REALIZADO FIXA']);
      }

      query = filtrarPeloLider(query, profile, await equipesDoLider(profile));
      const { data, error } = await query
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true })
        .order('entrada_coluna_em', { ascending: true });

      if (error) {
        onQueryError(error);
        throw error;
      }

      onQuerySuccess();

      // Processar para adicionar is_first_of_cnpj (agrupar visualmente por CNPJ)
      let lastCnpj = '';
      let lastPedido = '';
      let itemIdx = 0;
      type Linha = { subtotal?: number | null; coluna_atual?: string | null; quadro_atual?: string | null; status_id?: string | null; data_agendamento?: string | null };
      const itensProcessados: ItemTriagem[] = (data || []).map(item => {
        const extra = item as typeof item & Linha;
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        const isFirstPedido = item.pedido_id !== lastPedido;
        lastPedido = item.pedido_id || '';
        itemIdx = isFirstPedido ? 1 : itemIdx + 1;
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          numero_cotacao_operadora: String(item.numero_cotacao_operadora || ''),
          produto: item.produto || '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal: Number(extra.subtotal) || (Number(item.plano_valor) * Number(item.quantidade || 1)),
          entrada_coluna_em: item.entrada_coluna_em || '',
          coluna_atual: extra.coluna_atual || '',
          quadro_atual: extra.quadro_atual || '',
          status_id: extra.status_id || '',
          data_agendamento: extra.data_agendamento || null,
          is_first_of_cnpj: isFirst,
          is_first_of_pedido: isFirstPedido,
          item_index: itemIdx,
        };
      });

      return itensProcessados;
    },
    refetchInterval,
  });

  /* A fila é por LINHA e o clique leva as linhas do pedido nesta coluna: só
     elas saem (ou descem) na hora (`triagem/acaoTriagem.ts`). */
  const refletirClique = (alvo: { pedidoId: string; itens: string[] }, modo: 'sai' | 'desce') =>
    void depoisDoClique(queryClient, ['triagem-solar'], alvo, modo);

  const pedidos = useMemo(() => agruparPorPedido(itens), [itens]);

  const linhasDoPedido = (itemId: string) => {
    const clicado = itens.find(i => i.item_id === itemId);
    if (!clicado) throw new Error('Pedido não encontrado na fila. Atualize a triagem.');
    const linhas = itens.filter(i => i.pedido_id === clicado.pedido_id && i.coluna_atual === clicado.coluna_atual);
    return { clicado, linhas, ids: linhas.map(i => i.item_id) };
  };

  const atualizarLinhasIds = async (ids: string[], campos: Record<string, unknown>, colunas: string[] = COLUNAS_INPUT) => {
    const { data, error } = await supabase
      .from('itens_pedido')
      .update(campos as never)
      .in('id', ids)
      .in('coluna_atual', colunas)
      .select('id');
    if (error) throw error;
    const movidos = ((data as { id: string }[] | null) ?? []).map((r) => r.id);
    if (movidos.length === 0) throw new Error('O pedido já saiu desta fila. Atualize a triagem.');
    return movidos;
  };
  const atualizarLinhas = async (ids: string[], campos: Record<string, unknown>, colunas: string[] = COLUNAS_INPUT) =>
    (await atualizarLinhasIds(ids, campos, colunas)).length;

  /* O "de onde saiu" do histórico vem da própria fila (as linhas que o UPDATE
     acabou de mexer, que estavam nesta coluna), sem uma ida a mais ao banco
     para fotografar antes. */
  const fotoDasLinhas = (linhas: ItemTriagem[], movidos: string[]) =>
    linhas.filter((i) => movidos.includes(i.item_id));

  // Mutation para mover para ERRO SOLAR — o pedido inteiro
  const moverParaErro = useMutation({
    mutationFn: async (itemId: string) => {
      const { clicado, ids } = linhasDoPedido(itemId);
      const count = await atualizarLinhas(ids, { status_id: STATUS_ERRO_SOLAR });
      return { count, numero: clicado.numero_pedido, pedidoId: clicado.pedido_id, ids };
    },
    onSuccess: ({ count, numero, pedidoId, ids }) => {
      toast({
        title: 'Erro Solar',
        description: `${rotuloPedido(count, numero)} foi para o Erro Solar.`,
      });
      refletirClique({ pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao mover para ERRO SOLAR:', error);
      toast({
        title: 'Erro ao mover item',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para mover para o final da fila (MANTER) — o pedido inteiro
  const moverParaFinal = useMutation({
    mutationFn: async (itemId: string) => {
      const { clicado, ids } = linhasDoPedido(itemId);
      const count = await atualizarLinhas(ids, { manter_em: new Date().toISOString() });
      return { count, numero: clicado.numero_pedido, pedidoId: clicado.pedido_id, ids };
    },
    onSuccess: ({ count, numero, pedidoId, ids }) => {
      toast({
        title: 'Fim da fila',
        description: `${rotuloPedido(count, numero)} foi para o fim da fila.`,
      });
      refletirClique({ pedidoId, itens: ids }, 'desce');
    },
    onError: (error) => {
      console.error('Erro ao mover para o final:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para confirmar a cotação — um número para o pedido inteiro
  const confirmarCotacao = useMutation({
    mutationFn: async ({ itemId, numeroCotacao }: { itemId: string; numeroCotacao: string }) => {
      const { clicado, ids } = linhasDoPedido(itemId);
      const count = await atualizarLinhas(ids, {
        numero_cotacao_operadora: numeroCotacao,
        status_id: STATUS_VALIDACAO_PENDENTE,
      });
      return { count, numero: clicado.numero_pedido, numeroCotacao, pedidoId: clicado.pedido_id, ids };
    },
    onSuccess: ({ count, numero, numeroCotacao, pedidoId, ids }) => {
      toast({
        title: 'Cotação confirmada',
        description: `${rotuloPedido(count, numero)} seguiu para a Validação com a cotação ${numeroCotacao}.`,
      });
      refletirClique({ pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao confirmar cotação:', error);
      toast({
        title: 'Erro ao confirmar cotação',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para mover para TT RECURSOS INVÁLIDOS (crédito negado/dívida) — todos da mesma cotação
  //
  // (Douglas 05/08/2026) NÃO pede mais o valor da dívida. Quem move o item pra
  // cá ainda não sabe quanto é: o valor só aparece depois, quando o GC responde
  // o e-mail com a fatura do CPF. O campo era obrigatório (Confirmar ficava
  // desabilitado com valor <= 0) e obrigava a inventar um número — que ninguém
  // lia: `valor_divida` era gravado e não tinha UM leitor no sistema inteiro.
  const moverParaCreditoNegado = useMutation({
    mutationFn: async (itemId: string) => {
      const { clicado, linhas, ids } = linhasDoPedido(itemId);
      const userId = await meuUsuarioId();
      const movidos = await atualizarLinhasIds(ids, { status_id: STATUS_TT_RECURSOS_INVALIDOS });
      const count = movidos.length;
      await supabase.from('historico_status').insert(fotoDasLinhas(linhas, movidos).map((item) => ({
        pedido_id: item.pedido_id,
        item_id: item.item_id,
        quadro_anterior: item.quadro_atual,
        quadro_atual: 'TRAMITACAO_VIVO',
        coluna_anterior: item.coluna_atual,
        coluna_atual: '(TT) RECURSOS INVALIDOS',
        status_anterior_id: item.status_id,
        status_atual_id: STATUS_TT_RECURSOS_INVALIDOS,
        user_id: userId,
        motivo: 'Crédito Negado - Dívida Interna',
      })) as never);
      return { count, numero: clicado.numero_pedido, pedidoId: clicado.pedido_id, ids };
    },
    onSuccess: ({ count, numero, pedidoId, ids }) => {
      toast({
        title: 'Crédito negado',
        description: `${rotuloPedido(count, numero)} foi para TT Recursos Inválidos. O valor da dívida vem do GC.`,
        variant: 'destructive',
      });
      refletirClique({ pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao mover para crédito negado:', error);
      toast({
        title: 'Erro ao mover item',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para mover para CREDITO REPROVADO (quadro TRAMITACAO_PRIME) — exclusivo MÓVEL — o pedido inteiro
  const moverParaCreditoReprovado = useMutation({
    mutationFn: async ({ itemId, motivo, cotacao: cotacaoInformada }: { itemId: string; motivo: string; cotacao?: string }) => {
      if (!motivo.trim()) throw new Error('Informe o motivo real do retorno da Claro.');
      const { clicado, linhas } = linhasDoPedido(itemId);

      // A defesa é registrada por cotação. Pedido que entra sem número nunca
      // consegue ser defendido depois — por isso o número é exigido aqui.
      const cotacaoDoPedido = linhas.map(i => i.numero_cotacao_operadora?.trim()).find(Boolean) || '';
      const cotacao = cotacaoDoPedido || cotacaoInformada?.trim() || '';
      if (!cotacao) throw new Error('Informe o número da cotação da operadora antes de reprovar o crédito.');
      const ids = linhas.filter(i => i.coluna_atual === 'INPUT REALIZADO MOVEL').map(i => i.item_id);
      const semCotacao = linhas.filter(i => !i.numero_cotacao_operadora?.trim()).map(i => i.item_id);

      const userId = await meuUsuarioId();
      if (semCotacao.length) {
        const { error: erroCotacao } = await supabase.from('itens_pedido')
          .update({ numero_cotacao_operadora: cotacao }).in('id', semCotacao).eq('coluna_atual', 'INPUT REALIZADO MOVEL');
        if (erroCotacao) throw erroCotacao;
      }
      const movidos = await atualizarLinhasIds(ids, { status_id: STATUS_CREDITO_REPROVADO, credito_motivo_reprovacao: motivo.trim() }, ['INPUT REALIZADO MOVEL']);
      const count = movidos.length;
      await supabase.from('historico_status').insert(fotoDasLinhas(linhas, movidos).map((item) => ({
        pedido_id: item.pedido_id,
        item_id: item.item_id,
        quadro_anterior: item.quadro_atual,
        quadro_atual: 'TRAMITACAO_PRIME',
        coluna_anterior: item.coluna_atual,
        coluna_atual: 'CREDITO REPROVADO',
        status_anterior_id: item.status_id,
        status_atual_id: STATUS_CREDITO_REPROVADO,
        user_id: userId,
        motivo: `Retorno da análise inicial da Claro: ${motivo.trim()}`,
      })) as never);

      return { count, numero: clicado.numero_pedido, pedidoId: clicado.pedido_id, ids: movidos };
    },
    onSuccess: ({ count, numero, pedidoId, ids }) => {
      toast({
        title: 'Crédito reprovado',
        description: `${rotuloPedido(count, numero)} foi para Crédito reprovado (quadro Crédito).`,
      });
      refletirClique({ pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao mover para CREDITO REPROVADO:', error);
      toast({
        title: 'Erro ao mover item',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Em instalação — exclusivo FIXA (Douglas 29/09/2026). Sai do input para a
  // fila de instalação com data e turno; o pedido inteiro vai junto (Mesh e TV
  // acompanham a fibra). A trava de data e de quem pode fica no banco.
  const entrarEmInstalacao = useMutation({
    mutationFn: async ({ itemId, dataIso }: { itemId: string; dataIso: string | null }) => {
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        fn: string, args: Record<string, unknown>,
      ) => Promise<{ data: number | null; error: Error | null }>;
      const { clicado, ids } = linhasDoPedido(itemId);
      const { data, error } = await rpc('fn_em_instalacao_entrar', { p_itens: ids, p_data: dataIso });
      if (error) throw error;
      return { count: data ?? 0, pedidoId: clicado.pedido_id, ids };
    },
    onSuccess: ({ count, pedidoId, ids }) => {
      toast({
        title: 'Em instalação',
        description: count > 1 ? `${count} itens do pedido foram para a fila Em instalação.` : 'O pedido foi para a fila Em instalação.',
      });
      queryClient.invalidateQueries({ queryKey: ['triagem-em-instalacao'] });
      refletirClique({ pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'Não foi para Em instalação',
        description: (error as { message?: string } | null)?.message || 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Quem manda para Em instalação (Douglas 26/10/2026): a régua é a do banco
  // (fn_pode_em_instalacao). No CPF a supervisora da equipe CPF também pode.
  // Enquanto não responde, o botão fica aceso e a trava continua no banco.
  const { data: quemPode } = useQuery({
    queryKey: ['em-instalacao-quem-pode', profile?.id],
    enabled: familia === 'FIXA' && !!profile?.id,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<{ PJ: boolean; PF: boolean }> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        fn: string, args?: Record<string, unknown>,
      ) => Promise<{ data: { PJ: boolean; PF: boolean } | null; error: Error | null }>;
      const { data, error } = await rpc('fn_em_instalacao_quem_pode');
      if (error) throw error;
      return { PJ: !!data?.PJ, PF: !!data?.PF };
    },
  });
  const podeEmInstalacao = (tipoPessoa: string | null) =>
    !quemPode || (tipoPessoa === 'PF' ? quemPode.PF : quemPode.PJ);

  return {
    itens,
    pedidos,
    isLoading,
    refetch,
    podeEmInstalacao,
    moverParaErro: moverParaErro.mutateAsync,
    moverParaFinal: moverParaFinal.mutateAsync,
    confirmarCotacao: confirmarCotacao.mutateAsync,
    moverParaCreditoNegado: moverParaCreditoNegado.mutateAsync,
    moverParaCreditoReprovado: moverParaCreditoReprovado.mutate,
    moverParaCreditoReprovadoAsync: moverParaCreditoReprovado.mutateAsync,
    entrarEmInstalacao: entrarEmInstalacao.mutateAsync,
    isMovingToErro: moverParaErro.isPending,
    isMovingToFinal: moverParaFinal.isPending,
    isConfirmingCotacao: confirmarCotacao.isPending,
    isMovingToCreditoNegado: moverParaCreditoNegado.isPending,
    isMovingToCreditoReprovado: moverParaCreditoReprovado.isPending,
    isEntrandoInstalacao: entrarEmInstalacao.isPending,
  };
}
